<template>
  <q-dialog
    ref="dialogRef"
    position="right"
    full-height
    @hide="onDialogHide">
    <q-card class="all-recents-dialog column no-wrap">
      <div class="all-recents-dialog__head">
        <div>
          <div class="all-recents-dialog__title">{{ t('crudmanager.sidebar.allRecents') }}</div>
          <div class="all-recents-dialog__subtitle">
            {{ allRecents.length }} {{ t('crudmanager.sidebar.items') }}
            <template v-if="lastAccess">
              · {{ t('crudmanager.sidebar.lastAccess') }} {{ lastAccess }}
            </template>
          </div>
        </div>
        <div class="all-recents-dialog__head-actions">
          <q-btn
            flat
            dense
            round
            :icon="symRoundedClose"
            @click="onDialogCancel" />
        </div>
      </div>

      <div class="all-recents-dialog__filters">
        <div class="filter-row">
          <div class="filter-label">{{ t('crudmanager.sidebar.period') }}</div>
          <div class="chip-group">
            <button
              v-for="p in periodOptions"
              :key="p.value"
              type="button"
              :class="['chip', { 'is-active': period === p.value }]"
              @click="period = p.value">
              {{ p.label }}
            </button>
          </div>
        </div>

        <div class="filter-row filter-row--search">
          <div class="search-bar">
            <div class="search-input">
              <q-icon
                :name="symRoundedSearch"
                class="search-input__icon" />
              <input
                v-model="searchQuery"
                type="text"
                class="search-input__field"
                :placeholder="t('crudmanager.sidebar.searchRecents')" />
              <button
                v-if="searchQuery"
                type="button"
                class="search-input__clear"
                @click="searchQuery = ''">
                <q-icon :name="symRoundedClose" />
              </button>
            </div>
            <q-select
              v-model="typeFilter"
              :options="typeOptions"
              emit-value
              map-options
              dense
              outlined
              class="type-select"
              :dropdown-icon="symRoundedArrowDropDown"
              :placeholder="t('crudmanager.sidebar.all')">
              <template #selected-item="scope">
                <div
                  v-if="scope.opt.value"
                  class="type-select__selected">
                  <q-icon
                    v-if="scope.opt.icon"
                    :name="scope.opt.icon"
                    class="type-select__icon" />
                  <span>{{ scope.opt.label }}</span>
                </div>
                <div
                  v-else
                  class="type-select__selected is-placeholder">
                  {{ t('crudmanager.sidebar.allTypes') }}
                </div>
              </template>
              <template #option="scope">
                <q-item
                  v-bind="scope.itemProps"
                  class="type-select__option">
                  <q-item-section
                    v-if="scope.opt.icon"
                    avatar>
                    <q-icon :name="scope.opt.icon" />
                  </q-item-section>
                  <q-item-section>{{ scope.opt.label }}</q-item-section>
                </q-item>
              </template>
            </q-select>
          </div>
        </div>
      </div>

      <q-scroll-area class="all-recents-dialog__scroll col">
        <div
          v-if="filteredRecents.length === 0"
          class="all-recents-dialog__empty">
          {{ t('crudmanager.sidebar.noRecents') }}
        </div>

        <div
          v-for="instance in filteredRecents"
          :key="instance.id"
          class="recent-row-wrap">
          <button
            type="button"
            class="recent-card"
            @click="openInstance(instance)">
          <div
            class="recent-card__icon-wrap"
            :class="{ 'is-draft': isDraft(instance) }">
            <q-icon
              :name="modelIcon(instance) || symRoundedInbox"
              class="recent-card__icon" />
          </div>
          <div class="recent-card__body">
            <div class="recent-card__title">
              <span class="recent-card__instance">{{ instanceTitle(instance) || modelLabel(instance) }}</span>
            </div>
            <div class="recent-card__meta">
              <span
                class="recent-card__type-badge"
                :class="{ 'is-draft': isDraft(instance) }">
                <q-icon
                  v-if="modelIcon(instance)"
                  :name="modelIcon(instance) as string"
                  class="recent-card__type-badge-icon" />
                {{ modelLabel(instance) }}
                <template v-if="isDraft(instance)">&nbsp;{{ t('crudmanager.sidebar.draft') }}</template>
              </span>
              <span
                v-if="metaSubtitleText(instance)"
                class="recent-card__sep">·</span>
              <span
                v-if="metaSubtitleText(instance)"
                class="recent-card__status">
                <span
                  class="recent-card__dot"
                  :style="{ background: dotColor(instance) }"></span>
                {{ metaSubtitleText(instance) }}
              </span>
              <span
                v-if="instance.openedAt"
                class="recent-card__time">{{ relativeTime(instance.openedAt as unknown as string) }}</span>
            </div>
          </div>
          </button>
          <button
            type="button"
            class="recent-row-wrap__close"
            :aria-label="t('crudmanager.common.close_instance')"
            @click.stop.prevent="removeRecent(instance, $event)"
            @mousedown.stop.prevent>
            <q-icon :name="symRoundedClose" />
            <q-tooltip :delay="350">{{ t('crudmanager.common.close_instance') }}</q-tooltip>
          </button>
        </div>
      </q-scroll-area>

      <div class="all-recents-dialog__foot">
        <div class="all-recents-dialog__foot-left">
          <kbd class="all-recents-dialog__kbd">Esc</kbd>
          <span>{{ t('crudmanager.sidebar.toClose') }}</span>
        </div>
        <q-btn
          v-if="filteredRecents.length > 0"
          flat
          dense
          round
          :icon="symRoundedCleaningServices"
          class="all-recents-dialog__sweep">
          <q-tooltip :delay="350">{{ t('crudmanager.sidebar.closeAll') }}</q-tooltip>
          <q-popup-proxy
            ref="sweepPopup"
            anchor="top right"
            self="bottom right"
            :offset="[0, 6]">
            <div class="sweep-confirm">
              <div class="sweep-confirm__text">
                {{ t('crudmanager.sidebar.closeAllConfirm', { count: filteredRecents.length }) }}
              </div>
              <div class="sweep-confirm__actions">
                <q-btn
                  flat
                  dense
                  no-caps
                  :label="t('crudmanager.sidebar.cancel')"
                  class="sweep-confirm__cancel"
                  @click="sweepPopup?.hide()" />
                <q-btn
                  unelevated
                  dense
                  no-caps
                  color="negative"
                  :label="t('crudmanager.sidebar.confirmClose')"
                  @click="confirmCloseAll" />
              </div>
            </div>
          </q-popup-proxy>
        </q-btn>
      </div>
    </q-card>
  </q-dialog>
</template>

<script setup lang="ts">
import { symRoundedArrowDropDown, symRoundedCleaningServices, symRoundedClose, symRoundedInbox, symRoundedSearch } from '@quasar/extras/material-symbols-rounded'
import { useDialogPluginComponent } from 'quasar'
import useCrudUtils from 'src/CrudManager/Core/composables/CrudUtils/useCrudUtils'
import useDate from 'src/CrudManager/Core/composables/useDate'
import { getAllPreviews } from 'src/CrudManager/Core/composites/Editors/CrudPreview/PreviewDB'
import { useCrudManagerStore } from 'src/CrudManager/Core/stores/useCrudManagerStore'
import type { CrudBadgeSubtitle, CrudInstance } from 'src/CrudManager/Core/types/AdvancedCrudManager'
import type { QPopupProxy } from 'quasar'
import { computed, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'

type Period = 'all' | 'today' | 'week' | 'draft'

const props = defineProps<{
  initialPeriod?: Period
}>()

defineEmits([...useDialogPluginComponent.emits])

const { t } = useI18n()
const router = useRouter()
const crudManagerStore = useCrudManagerStore()
const { generateModuleUrl } = useCrudUtils()
const { getRelativeTime } = useDate()
const { dialogRef, onDialogHide, onDialogCancel } = useDialogPluginComponent()
const sweepPopup = ref<QPopupProxy | null>(null)

const confirmCloseAll = async () => {
  sweepPopup.value?.hide()
  await closeAllVisible()
}

const period = ref<Period>(props.initialPeriod ?? 'all')
const typeFilter = ref<string | null>(null)
const searchQuery = ref('')
const previewTitles = ref<Map<string, { title: string; model: string }>>(new Map())

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

onMounted(async () => {
  try {
    previewTitles.value = await getAllPreviews()
  } catch (e) {
    console.warn('Failed to load preview titles for recents search:', e)
  }
})

function collectUuids(value: unknown, acc: Set<string>, depth = 0): void {
  if (!value || depth > 3) return
  if (typeof value === 'string') {
    if (UUID_RE.test(value)) acc.add(value)
    return
  }
  if (Array.isArray(value)) {
    for (const v of value) collectUuids(v, acc, depth + 1)
    return
  }
  if (typeof value === 'object') {
    for (const v of Object.values(value as Record<string, unknown>)) collectUuids(v, acc, depth + 1)
  }
}

const normalize = (s: string) =>
  s
    .toLocaleLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')

const typeOptions = computed(() => [
  { value: null, label: t('crudmanager.sidebar.allTypes'), icon: undefined as string | undefined },
  ...modelTypes.value.map((m) => ({ value: m.name, label: m.label, icon: m.icon })),
])

const periodOptions = computed<{ value: Period; label: string }[]>(() => [
  { value: 'all', label: t('crudmanager.sidebar.all') },
  { value: 'today', label: t('crudmanager.sidebar.today') },
  { value: 'week', label: t('crudmanager.sidebar.thisWeek') },
  { value: 'draft', label: t('crudmanager.sidebar.drafts') },
])

const isRecent = (i: CrudInstance) =>
  !!i.openedAt && (i.moduleName === 'editor' || i.moduleName === 'creator' || i.moduleName === 'external')

const isDraft = (i: CrudInstance) => {
  // Creator: in-progress draft once the user has interacted with the form.
  if (i.moduleName === 'creator') {
    return i.status?.userTouched === true
  }
  // Editor: legacy fallback — server-side draft surfaces in the subtitle text.
  if (i.moduleName === 'editor') {
    const rawSub: unknown = i.subtitle
    const sub = rawSub && typeof rawSub === 'object' ? (rawSub as CrudBadgeSubtitle) : null
    const txt = (sub?.text || '').toLowerCase()
    return txt.includes('brouillon') || txt.includes('draft')
  }
  return false
}

const allRecents = computed(() =>
  crudManagerStore.crudInstances
    .filter(isRecent)
    .slice()
    .sort((a, b) => new Date(b.openedAt || 0).getTime() - new Date(a.openedAt || 0).getTime()),
)

const lastAccess = computed(() => {
  const first = allRecents.value[0]
  return first?.openedAt ? getRelativeTime(first.openedAt as unknown as string) : ''
})

const modelTypes = computed(() => {
  const map = new Map<string, { name: string; label: string; icon?: string }>()
  if (!crudManagerStore.isInitiated) return []
  for (const i of allRecents.value) {
    if (!map.has(i.modelName)) {
      const meta = crudManagerStore.getModelMeta(i.modelName)
      map.set(i.modelName, {
        name: i.modelName,
        label: t(`crudmodels.${i.modelName}.name`, 2),
        icon: meta?.icon,
      })
    }
  }
  return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label))
})

const isWithinPeriod = (iso: string | null, p: Period) => {
  if (!iso) return false
  const d = new Date(iso)
  const now = new Date()
  if (p === 'today') {
    return d.toDateString() === now.toDateString()
  }
  if (p === 'week') {
    const diffMs = now.getTime() - d.getTime()
    return diffMs <= 7 * 24 * 60 * 60 * 1000
  }
  return true
}

const filteredRecents = computed(() => {
  const needle = searchQuery.value.trim() ? normalize(searchQuery.value.trim()) : ''
  return allRecents.value.filter((i) => {
    if (period.value === 'draft') {
      if (!isDraft(i)) return false
    } else if (!isWithinPeriod(i.openedAt as unknown as string | null, period.value)) {
      return false
    }
    if (typeFilter.value && i.modelName !== typeFilter.value) return false
    if (needle) {
      const parts: string[] = [
        i.title ?? '',
        t(`crudmodels.${i.modelName}.name`, 1),
        t(`crudmodels.${i.modelName}.name`, 2),
        i.modelName,
        subtitleText(i),
      ]
      const item = i.data?.item as Record<string, unknown> | undefined
      if (item) {
        for (const v of Object.values(item)) {
          if (typeof v === 'string' || typeof v === 'number') parts.push(String(v))
        }
        const uuids = new Set<string>()
        collectUuids(item, uuids)
        for (const id of uuids) {
          const preview = previewTitles.value.get(id)
          if (preview) parts.push(preview.title)
        }
      }
      const haystack = parts.map(normalize).join(' ')
      if (!haystack.includes(needle)) return false
    }
    return true
  })
})

const modelLabel = (instance: CrudInstance) => t(`crudmodels.${instance.modelName}.name`, 1)

// Title follows whatever the model declares via getTitle / getSubtitle — no
// scraping of arbitrary item fields. For drafts we prefer the live subtitle
// (model-defined identifier like "Bon de travail #22026") over the static
// getTitle output ("Créer une nouvelle transaction").
const instanceTitle = (instance: CrudInstance) => {
  if (isDraft(instance)) return subtitleText(instance) || instance.title
  return instance.title
}
const modelIcon = (instance: CrudInstance) => {
  if (!crudManagerStore.isInitiated) return undefined
  return crudManagerStore.getModelMeta(instance.modelName)?.icon
}

const asSubtitleObject = (s: unknown): CrudBadgeSubtitle | null =>
  s && typeof s === 'object' ? (s as CrudBadgeSubtitle) : null

const subtitleText = (instance: CrudInstance) => {
  const obj = asSubtitleObject(instance.subtitle)
  if (obj?.text) return obj.text
  if (typeof instance.subtitle === 'string') return instance.subtitle
  return ''
}

// Drafts already render the subtitle inside the title row ("Brouillon · Bon ·
// Bon de travail #22021"), so suppress the duplicate in the meta line.
const metaSubtitleText = (instance: CrudInstance) => {
  if (isDraft(instance)) return ''
  return subtitleText(instance)
}

const dotColor = (instance: CrudInstance) => {
  const obj = asSubtitleObject(instance.subtitle)
  const c = obj?.color || null
  if (c && typeof window !== 'undefined') {
    const v = getComputedStyle(document.documentElement).getPropertyValue(`--q-${c}`).trim()
    if (v) return v.startsWith('#') ? v : `rgb(${v})`
    return c
  }
  return 'var(--q-primary)'
}

const relativeTime = (iso: string) => getRelativeTime(iso)

const openInstance = (instance: CrudInstance) => {
  const url = generateModuleUrl(instance.modelName, instance.moduleName, instance.props?.modelId as string)
  if (url) {
    router.push(url)
    onDialogCancel()
  }
}

const isRouteInsideInstance = (instance: CrudInstance) => {
  const url = generateModuleUrl(instance.modelName, instance.moduleName, instance.props?.modelId as string)
  if (!url) return false
  return router.currentRoute.value.path === url
}

const pickFallbackModel = (removed: CrudInstance[]): string | null => {
  for (let i = removed.length - 1; i >= 0; i--) {
    const entry = removed[i]
    if (!entry) continue
    if (!entry.parentId) return entry.modelName
    const parent = crudManagerStore.crudInstances.find((p) => p.id === entry.parentId)
    if (parent) return parent.modelName
    return entry.modelName
  }
  return null
}

const redirectAfterClear = async (removed: CrudInstance[]) => {
  const shouldRedirect = removed.some(isRouteInsideInstance)
  if (!shouldRedirect) return
  const fallbackModel = pickFallbackModel(removed)
  if (!fallbackModel) return
  const lister = crudManagerStore.crudInstances.find((i) => i.modelName === fallbackModel && i.moduleName === 'lister')
  const url = generateModuleUrl(fallbackModel, 'lister', '')
  if (!url) return
  if (lister) lister.status.isVisible = true
  await router.push(url)
}

const removeRecent = async (instance: CrudInstance, event?: Event) => {
  event?.stopPropagation()
  event?.preventDefault()
  const parent = instance.parentId ? crudManagerStore.crudInstances.find((i) => i.id === instance.parentId) : null
  const wasParentVisible = !!parent?.status.isVisible
  const wasOnInstance = isRouteInsideInstance(instance)
  crudManagerStore.removeInstance(instance, false)
  if (parent && wasParentVisible) parent.status.isVisible = true
  if (wasOnInstance) await redirectAfterClear([instance])
}

const closeAllVisible = async () => {
  const list = filteredRecents.value.slice()
  const parentsToRestore = new Set<string>()
  for (const i of list) {
    if (i.parentId) {
      const parent = crudManagerStore.crudInstances.find((p) => p.id === i.parentId)
      if (parent?.status.isVisible) parentsToRestore.add(parent.id)
    }
  }
  for (const i of list) {
    await crudManagerStore.removeInstance(i, false)
  }
  parentsToRestore.forEach((pid) => {
    const parent = crudManagerStore.crudInstances.find((p) => p.id === pid)
    if (parent) parent.status.isVisible = true
  })
  await redirectAfterClear(list)
  onDialogCancel()
}
</script>

<style lang="scss" scoped>
.all-recents-dialog {
  width: 380px;
  max-width: 92vw;
  display: flex;
  flex-direction: column;
  background: var(--q-dark);
  color: #ffffff;
}

.body--light .all-recents-dialog {
  background: #ffffff;
  color: #0f1419;
}

.all-recents-dialog__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 20px 20px 12px;
}

.all-recents-dialog__head-actions {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
}


.all-recents-dialog__title {
  font-size: 1.05rem;
  font-weight: 700;
  letter-spacing: 0.01em;
}

.all-recents-dialog__subtitle {
  margin-top: 2px;
  font-size: 0.75rem;
  color: $grey-6;
}

.all-recents-dialog__filters {
  padding: 4px 20px 8px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.filter-row {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 6px;
  min-width: 0;
}

.filter-label {
  font-size: 0.62rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: $grey-6;
}

.search-bar {
  display: flex;
  align-items: stretch;
  gap: 8px;
  min-width: 0;
}

.search-input {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.06);
  flex: 1 1 auto;
  min-width: 0;
  transition:
    border-color 0.15s cubic-bezier(0.4, 0, 0.2, 1),
    background-color 0.15s cubic-bezier(0.4, 0, 0.2, 1);

  &:focus-within {
    border-color: color-mix(in srgb, var(--q-primary) 55%, transparent);
    background: color-mix(in srgb, var(--q-primary) 8%, transparent);
  }
}

.body--light .search-input {
  background: rgba(0, 0, 0, 0.04);
  border-color: rgba(0, 0, 0, 0.08);
}

.search-input__icon {
  font-size: 1rem;
  opacity: 0.6;
  flex-shrink: 0;
}

.search-input__field {
  flex: 1 1 auto;
  border: none;
  outline: none;
  background: transparent;
  color: inherit;
  font-family: inherit;
  font-size: 0.82rem;
  min-width: 0;

  &::placeholder {
    color: currentColor;
    opacity: 0.45;
  }
}

.search-input__clear {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  color: inherit;
  opacity: 0.6;
  cursor: pointer;
  padding: 2px;
  border-radius: 4px;
  transition: opacity 0.15s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    opacity: 1;
  }

  .q-icon {
    font-size: 0.9rem;
  }
}

.type-select {
  flex: 0 0 auto;
  width: 168px;
  min-width: 0;

  :deep(.q-field__control) {
    min-height: 36px;
    padding: 0 8px;
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.05);
  }

  :deep(.q-field__native) {
    padding: 0;
    min-height: 34px;
    font-size: 0.8rem;
    flex-wrap: nowrap;
    min-width: 0;
  }

  :deep(.q-field__append) {
    padding-left: 4px;
    height: 34px;
  }

  :deep(.q-field__control:before) {
    border-color: rgba(255, 255, 255, 0.06);
  }
}

.body--light .type-select :deep(.q-field__control) {
  background: rgba(0, 0, 0, 0.04);
}

.type-select__selected {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.78rem;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    min-width: 0;
  }

  &.is-placeholder {
    opacity: 0.55;
  }
}

.type-select__icon {
  font-size: 0.95rem;
  opacity: 0.85;
  flex-shrink: 0;
}

.type-select__option .q-icon {
  font-size: 1rem;
}

.chip-group {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: transparent;
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.72rem;
  font-weight: 500;
  cursor: pointer;
  transition:
    background-color 0.15s cubic-bezier(0.4, 0, 0.2, 1),
    border-color 0.15s cubic-bezier(0.4, 0, 0.2, 1),
    color 0.15s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    background: rgba(255, 255, 255, 0.05);
    color: #fff;
  }

  &.is-active {
    background: color-mix(in srgb, var(--q-primary) 20%, transparent);
    border-color: color-mix(in srgb, var(--q-primary) 55%, transparent);
    color: var(--q-primary);
  }
}

.body--light .chip {
  border-color: rgba(0, 0, 0, 0.12);
  color: rgba(0, 0, 0, 0.7);

  &:hover {
    background: rgba(0, 0, 0, 0.04);
    color: #000;
  }
}

.chip__icon {
  font-size: 0.95rem;
}

.all-recents-dialog__scroll {
  padding: 6px 12px 16px;
}

.all-recents-dialog__empty {
  padding: 24px;
  text-align: center;
  color: $grey-6;
  font-size: 0.8rem;
}

.recent-row-wrap {
  position: relative;
}

.recent-card {
  display: grid;
  grid-template-columns: 36px 1fr;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 10px 44px 10px 10px;
  border: none;
  background: transparent;
  border-radius: 10px;
  color: inherit;
  text-align: left;
  cursor: pointer;
  transition: background-color 0.15s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    background: rgba(255, 255, 255, 0.05);
  }
}

.body--light .recent-card:hover {
  background: rgba(0, 0, 0, 0.04);
}

.recent-row-wrap__close {
  position: absolute;
  top: 50%;
  right: 8px;
  transform: translateY(-50%);
  width: 26px;
  height: 26px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: inherit;
  opacity: 0.55;
  cursor: pointer;
  transition: opacity 0.15s cubic-bezier(0.4, 0, 0.2, 1), background-color 0.15s cubic-bezier(0.4, 0, 0.2, 1);

  .q-icon {
    font-size: 1rem;
  }

  &:hover {
    opacity: 1;
    background: rgba(255, 255, 255, 0.08);
  }
}

.body--light .recent-row-wrap__close:hover {
  background: rgba(0, 0, 0, 0.06);
}

.recent-card__icon-wrap {
  width: 36px;
  min-height: 36px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.06);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4px 0;
  gap: 2px;
}

.body--light .recent-card__icon-wrap {
  background: rgba(0, 0, 0, 0.05);
}

.recent-card__icon-wrap.is-draft {
  background: color-mix(in srgb, var(--q-warning, #f59e0b) 18%, transparent);
  color: var(--q-warning, #f59e0b);
}

.recent-card__icon {
  font-size: 1.1rem;
  opacity: 0.85;
}

.recent-card__icon-wrap.is-draft .recent-card__icon {
  color: var(--q-warning, #f59e0b);
  opacity: 1;
}


.recent-card__body {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.recent-card__title {
  font-size: 0.85rem;
  font-weight: 600;
  display: flex;
  gap: 4px;
  align-items: baseline;
  flex-wrap: wrap;
}

.recent-card__model {
  color: inherit;
}

.recent-card__type-badge {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 1px 7px;
  border-radius: 999px;
  font-size: 0.66rem;
  font-weight: 600;
  background: rgba(255, 255, 255, 0.08);
  color: inherit;
  opacity: 0.85;
  line-height: 1.4;
}

.body--light .recent-card__type-badge {
  background: rgba(0, 0, 0, 0.05);
}

.recent-card__type-badge.is-draft {
  background: color-mix(in srgb, var(--q-warning, #f59e0b) 20%, transparent);
  color: var(--q-warning, #f59e0b);
  opacity: 1;
}

.recent-card__type-badge-icon {
  font-size: 0.85rem;
  opacity: 0.85;
}

.recent-card__instance {
  font-weight: 500;
}

.recent-card__sep {
  color: $grey-6;
  opacity: 0.6;
}

.recent-card__meta {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.7rem;
  color: $grey-6;
}

.recent-card__status {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.recent-card__dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--q-primary);
}

.recent-card__time {
  opacity: 0.85;
  margin-left: auto;
  white-space: nowrap;
}

.all-recents-dialog__foot {
  padding: 6px 12px 6px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  font-size: 0.7rem;
  color: $grey-6;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  min-height: 44px;
}

.all-recents-dialog__foot-left {
  display: flex;
  align-items: center;
  gap: 8px;
}

@media (hover: none) and (pointer: coarse) {
  .all-recents-dialog__foot-left {
    display: none;
  }
}

.all-recents-dialog__sweep {
  color: $grey-5;
  margin-left: auto;

  &:hover {
    color: var(--q-primary);
  }
}

.sweep-confirm {
  padding: 12px 14px;
  max-width: 240px;
  background: var(--q-dark);
  color: #ffffff;
  border-radius: 10px;
}

.body--light .sweep-confirm {
  background: #ffffff;
  color: #0f1419;
}

.sweep-confirm__text {
  font-size: 0.82rem;
  line-height: 1.35;
  margin-bottom: 10px;
}

.sweep-confirm__actions {
  display: flex;
  justify-content: flex-end;
  gap: 6px;
}

.sweep-confirm__cancel {
  font-size: 0.75rem;
  color: $grey-5;
}

.body--light .all-recents-dialog__foot {
  border-top-color: rgba(0, 0, 0, 0.06);
}

.all-recents-dialog__kbd {
  padding: 2px 6px;
  font-size: 0.65rem;
  font-family: inherit;
  background: rgba(255, 255, 255, 0.08);
  border-radius: 4px;
  border: 1px solid rgba(255, 255, 255, 0.12);
}

.body--light .all-recents-dialog__kbd {
  background: rgba(0, 0, 0, 0.05);
  border-color: rgba(0, 0, 0, 0.12);
}
</style>
